<template>
  <div class="vac">
    <div class="vac__aura" aria-hidden="true"></div>

    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" />
    </div>

    <div class="vac__body">
      <header class="cmd">
        <div class="cmd__id">
          <div class="cmd__glyph" aria-hidden="true">🚧</div>
          <div class="cmd__copy">
            <h1 class="cmd__title">Entradas y salidas</h1>
            <p class="cmd__sub">
              <span class="live" :class="{ 'live--on': autoRefresh }">
                <i aria-hidden="true"></i>{{ autoRefresh ? 'En vivo' : 'En pausa' }}
              </span>
              <span class="cmd__sep" aria-hidden="true"></span>
              Control de acceso vehicular
              <span v-if="sync.last_sync_at" class="cmd__stamp">
                Última lectura {{ formatClock(sync.last_sync_at) }}
              </span>
              <span v-else class="cmd__stamp cmd__stamp--off">Sin sincronizar</span>
            </p>
          </div>
        </div>

        <div class="cmd__actions">
          <label class="switch" title="Refresca el tablero automáticamente">
            <input type="checkbox" v-model="autoRefresh" />
            <span class="switch__track" aria-hidden="true"><span class="switch__knob"></span></span>
            <span class="switch__text">Auto</span>
          </label>
          <button class="act act--brand" :disabled="syncing" @click="syncNow">
            <span v-if="syncing" class="act__spin" aria-hidden="true"></span>
            {{ syncing ? 'Actualizando…' : 'Actualizar' }}
          </button>
          <button
            class="act act--ghost"
            :disabled="resyncing"
            title="Vuelve a vincular las credenciales con las unidades y el personal. Útil después de dar de alta unidades nuevas."
            @click="resyncCredentials"
          >
            {{ resyncing ? 'Vinculando…' : 'Re-vincular' }}
          </button>
          <button class="act act--ghost" @click="goBack">Volver</button>
        </div>
      </header>

      <div v-if="loading" class="scan" aria-hidden="true"></div>

      <transition name="drop">
        <p v-if="error" class="banner banner--error">{{ error }}</p>
      </transition>
      <transition name="drop">
        <p v-if="notice" class="banner banner--ok">{{ notice }}</p>
      </transition>
      <transition name="drop">
        <p v-if="sync.last_error" class="banner banner--warn">
          El controlador reportó: {{ sync.last_error }}
        </p>
      </transition>

      <section class="stats">
        <article class="stat stat--fleet">
          <header class="stat__head">
            <span class="stat__label">Unidades dentro</span>
            <span class="stat__glyph" aria-hidden="true">🚛</span>
          </header>
          <div class="stat__slot">
            <transition name="tick">
              <strong class="stat__value" :key="counts.vehicles_inside">{{ counts.vehicles_inside }}</strong>
            </transition>
          </div>
          <div class="meter" aria-hidden="true">
            <span class="meter__fill" :style="{ flexGrow: counts.vehicles_inside }"></span>
            <span class="meter__rest" :style="{ flexGrow: counts.vehicles_outside }"></span>
          </div>
          <span class="stat__hint">{{ counts.vehicles_outside }} fuera</span>
        </article>

        <article class="stat stat--crew">
          <header class="stat__head">
            <span class="stat__label">Personas dentro</span>
            <span class="stat__glyph" aria-hidden="true">👤</span>
          </header>
          <div class="stat__slot">
            <transition name="tick">
              <strong class="stat__value" :key="counts.people_inside">{{ counts.people_inside }}</strong>
            </transition>
          </div>
          <div class="meter" aria-hidden="true">
            <span class="meter__fill" :style="{ flexGrow: counts.people_inside }"></span>
            <span class="meter__rest" :style="{ flexGrow: counts.people_outside }"></span>
          </div>
          <span class="stat__hint">{{ counts.people_outside }} fuera</span>
        </article>

        <article class="stat stat--total">
          <header class="stat__head">
            <span class="stat__label">Total dentro</span>
            <span class="stat__glyph" aria-hidden="true">🛡️</span>
          </header>
          <div class="stat__slot">
            <transition name="tick">
              <strong class="stat__value" :key="counts.inside">{{ counts.inside }}</strong>
            </transition>
          </div>
          <div class="meter" aria-hidden="true">
            <span class="meter__fill" :style="{ flexGrow: counts.inside }"></span>
            <span class="meter__rest" :style="{ flexGrow: counts.outside }"></span>
          </div>
          <span class="stat__hint">de {{ counts.total }} credenciales</span>
        </article>

        <article class="stat stat--watch" :class="{ 'stat--alert': counts.stale_inside > 0 }">
          <header class="stat__head">
            <span class="stat__label">Sin salida</span>
            <span class="stat__glyph" aria-hidden="true">⏱️</span>
          </header>
          <div class="stat__slot">
            <transition name="tick">
              <strong class="stat__value" :key="counts.stale_inside">{{ counts.stale_inside }}</strong>
            </transition>
          </div>
          <div class="stat__flags">
            <span v-if="counts.stale_inside > 0" class="flag">Requiere revisión</span>
            <span v-else class="flag flag--calm">Todo en orden</span>
          </div>
          <span class="stat__hint">dentro más de {{ staleAfterHours }} h</span>
        </article>
      </section>

      <section class="bar">
        <div class="seg">
          <span
            class="seg__thumb"
            aria-hidden="true"
            :style="{ transform: view === 'log' ? 'translateX(100%)' : 'translateX(0)' }"
          ></span>
          <button :class="['seg__btn', { 'is-active': view === 'board' }]" @click="view = 'board'">
            Tablero
          </button>
          <button :class="['seg__btn', { 'is-active': view === 'log' }]" @click="switchToLog">
            Bitácora
          </button>
        </div>

        <div class="filters">
          <label class="field">
            <svg class="field__icon" viewBox="0 0 20 20" aria-hidden="true">
              <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" stroke-width="1.7" />
              <path d="M13.2 13.2 17 17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" />
            </svg>
            <input
              v-model="search"
              class="input field__input"
              type="search"
              placeholder="Buscar unidad, persona o gafete…"
              @input="onSearchInput"
            />
          </label>
          <select v-model="kind" class="input input--select" @change="reload">
            <option value="all">Todos</option>
            <option value="vehicle">Unidades</option>
            <option value="person">Personas</option>
            <option value="unknown">Sin clasificar</option>
          </select>
          <template v-if="view === 'log'">
            <input v-model="logStart" class="input input--date" type="date" @change="reloadLog" />
            <input v-model="logEnd" class="input input--date" type="date" @change="reloadLog" />
            <select v-model="logCategory" class="input input--select" @change="reloadLog">
              <option value="all">Todo el historial</option>
              <option value="credential">Lecturas de credencial</option>
              <option value="door">Movimientos de pluma</option>
              <option value="system">Eventos del sistema</option>
            </select>
            <select
              v-model="logDirection"
              class="input input--select"
              :disabled="logCategory !== 'all' && logCategory !== 'credential'"
              @change="reloadLog"
            >
              <option value="all">Entradas y salidas</option>
              <option value="in">Solo entradas</option>
              <option value="out">Solo salidas</option>
            </select>
          </template>
        </div>
      </section>

      <template v-if="view === 'board'">
        <section v-for="group in groups" :key="group.key" class="zone">
          <header class="zone__head">
            <span class="zone__glyph" aria-hidden="true">{{ group.icon }}</span>
            <h2 class="zone__title">{{ group.label }}</h2>
            <span class="zone__chips">
              <span class="chip chip--in">{{ group.inside.length }} dentro</span>
              <span class="chip chip--out">{{ group.outside.length }} fuera</span>
            </span>
          </header>

          <div class="lanes">
            <div class="lane lane--in" :class="{ 'is-live': autoRefresh }">
              <header class="lane__head">
                <h3 class="lane__title">Dentro</h3>
                <span class="pill pill--in">{{ group.inside.length }}</span>
              </header>
              <p v-if="!loading && !group.inside.length" class="empty">
                Sin {{ group.emptyNoun }} dentro.
              </p>
              <transition-group tag="ul" name="entry" class="entries">
                <li
                  v-for="row in group.inside"
                  :key="'in-' + row.credential_ident"
                  class="entry"
                  :class="{ 'entry--stale': row.is_stale }"
                >
                  <span class="entry__rail" aria-hidden="true"></span>
                  <span class="entry__badge" :class="'entry__badge--' + row.kind" aria-hidden="true">
                    {{ group.icon }}
                  </span>
                  <span class="entry__body">
                    <span class="entry__name">{{ row.display_name }}</span>
                    <span class="entry__meta">{{ subtitleFor(row) }}</span>
                  </span>
                  <span class="entry__when">
                    <span class="entry__clock">{{ formatClock(row.last_event_at) }}</span>
                    <span class="entry__ago">{{ formatElapsed(row.elapsed_minutes) }}</span>
                  </span>
                </li>
              </transition-group>
            </div>

            <div class="lane lane--out">
              <header class="lane__head">
                <h3 class="lane__title">Fuera</h3>
                <span class="pill pill--out">{{ group.outside.length }}</span>
              </header>
              <p v-if="!loading && !group.outside.length" class="empty">
                Sin {{ group.emptyNoun }} fuera.
              </p>
              <transition-group tag="ul" name="entry" class="entries">
                <li v-for="row in group.outside" :key="'out-' + row.credential_ident" class="entry">
                  <span class="entry__rail" aria-hidden="true"></span>
                  <span class="entry__badge" :class="'entry__badge--' + row.kind" aria-hidden="true">
                    {{ group.icon }}
                  </span>
                  <span class="entry__body">
                    <span class="entry__name">{{ row.display_name }}</span>
                    <span class="entry__meta">{{ subtitleFor(row) }}</span>
                  </span>
                  <span class="entry__when">
                    <span class="entry__clock">{{ formatClock(row.last_event_at) }}</span>
                    <span class="entry__ago">{{ formatElapsed(row.elapsed_minutes) }}</span>
                  </span>
                </li>
              </transition-group>
            </div>
          </div>
        </section>
      </template>

      <section v-else class="log">
        <div class="log__scroll">
          <table class="logtable">
            <thead>
              <tr>
                <th>Fecha y hora</th>
                <th>Evento</th>
                <th>Unidad / Persona</th>
                <th>Gafete</th>
                <th>Lector</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in events" :key="row.id" :class="{ 'is-context': row.category !== 'credential' }">
                <td class="mono">{{ formatDateTime(row.event_at) }}</td>
                <td>
                  <span
                    v-if="row.category === 'credential'"
                    class="tag"
                    :class="'tag--' + row.direction"
                  >
                    {{ directionLabel(row.direction) }}
                  </span>
                  <span v-else class="tag" :class="'tag--' + row.category">
                    {{ row.label }}
                  </span>
                </td>
                <td>
                  <template v-if="row.category === 'credential'">
                    <span class="cell-name">{{ row.display_name }}</span>
                    <span class="cell-kind">
                      {{ kindLabel(row.kind) }}
                      <template v-if="row.unit_description"> · {{ row.unit_description }}</template>
                    </span>
                  </template>
                  <span v-else class="cell-muted">{{ row.detail || '—' }}</span>
                </td>
                <td class="mono">{{ row.credential_ident || '—' }}</td>
                <td class="mono">{{ row.card_reader_no || '—' }}</td>
                <td>
                  <span v-if="row.category !== 'credential'" class="cell-muted">—</span>
                  <span
                    v-else-if="!row.is_granted"
                    class="tag tag--deny"
                    title="El controlador rechazó la credencial"
                  >
                    Rechazado
                  </span>
                  <span
                    v-else-if="!row.is_effective"
                    class="tag tag--repeat"
                    :title="`Lectura repetida dentro de los ${cooldownLabel} posteriores al paso anterior; no cambia el estado`"
                  >
                    Repetido
                  </span>
                  <span v-else class="tag tag--ok">Aceptado</span>
                </td>
              </tr>
              <tr v-if="!loading && !events.length" class="logtable__blank">
                <td colspan="6">
                  <p class="empty">Sin movimientos en el periodo seleccionado.</p>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="pager">
          <button class="act act--ghost act--sm" :disabled="offset === 0" @click="changePage(-1)">
            Anterior
          </button>
          <span class="pager__info mono">
            {{ eventsTotal ? offset + 1 : 0 }}–{{ Math.min(offset + limit, eventsTotal) }} de {{ eventsTotal }}
          </span>
          <button
            class="act act--ghost act--sm"
            :disabled="offset + limit >= eventsTotal"
            @click="changePage(1)"
          >
            Siguiente
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const AUTO_REFRESH_MS = 30000

export default {
  name: 'VehicleAccessControl',
  data() {
    return {
      view: 'board',
      loading: false,
      syncing: false,
      error: '',
      autoRefresh: true,
      search: '',
      kind: 'all',
      inside: [],
      outside: [],
      counts: {
        inside: 0,
        outside: 0,
        total: 0,
        vehicles_inside: 0,
        vehicles_outside: 0,
        people_inside: 0,
        people_outside: 0,
        stale_inside: 0,
      },
      staleAfterHours: 24,
      cooldownSeconds: 180,
      sync: { last_sync_at: null, last_error: '', last_event_num: 0 },
      events: [],
      eventsTotal: 0,
      limit: 50,
      offset: 0,
      logStart: '',
      logEnd: '',
      logDirection: 'all',
      logCategory: 'all',
      refreshTimer: null,
      searchTimer: null,
      resyncing: false,
      notice: '',
      noticeTimer: null,
    }
  },
  created() {
    const today = this.localDate(new Date())
    this.logStart = today
    this.logEnd = today
    this.fetchStatus()
    this.startAutoRefresh()
  },
  beforeUnmount() {
    this.stopAutoRefresh()
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this.noticeTimer) clearTimeout(this.noticeTimer)
  },
  computed: {
    /**
     * Units and people are tracked by the same barrier but read very
     * differently, so each gets its own inside/outside board. The unclassified
     * group only appears when the device reports a badge we cannot place.
     */
    groups() {
      const definitions = [
        { key: 'vehicle', label: 'Unidades', icon: '🚛', emptyNoun: 'unidades' },
        { key: 'person', label: 'Personas', icon: '👤', emptyNoun: 'personas' },
        { key: 'unknown', label: 'Sin clasificar', icon: '❔', emptyNoun: 'credenciales' },
      ]
      return definitions
        .filter((group) => this.kind === 'all' || this.kind === group.key)
        .map((group) => ({
          ...group,
          inside: this.inside.filter((row) => row.kind === group.key),
          outside: this.outside.filter((row) => row.kind === group.key),
        }))
        .filter(
          (group) =>
            group.key !== 'unknown' || group.inside.length > 0 || group.outside.length > 0,
        )
    },
    cooldownLabel() {
      const minutes = Math.round(this.cooldownSeconds / 60)
      if (minutes >= 1) return minutes === 1 ? '1 minuto' : `${minutes} minutos`
      return `${this.cooldownSeconds} segundos`
    },
  },
  watch: {
    autoRefresh(enabled) {
      if (enabled) this.startAutoRefresh()
      else this.stopAutoRefresh()
    },
  },
  methods: {
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    authHeaders() {
      return { Authorization: `Token ${localStorage.getItem('authToken')}` }
    },
    goBack() {
      this.$router.push({ path: '/logs' })
    },
    startAutoRefresh() {
      this.stopAutoRefresh()
      this.refreshTimer = setInterval(this.reload, AUTO_REFRESH_MS)
    },
    stopAutoRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },
    reload() {
      if (this.view === 'board') this.fetchStatus()
      else this.fetchEvents()
    },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.offset = 0
        this.reload()
      }, 350)
    },
    switchToLog() {
      this.view = 'log'
      this.offset = 0
      this.fetchEvents()
    },
    reloadLog() {
      this.offset = 0
      this.fetchEvents()
    },
    handleError(err, fallback) {
      if (err?.response?.status === 403) {
        this.error = 'No tienes permisos para ver el control de acceso vehicular.'
        return
      }
      this.error = err?.response?.data?.detail || fallback
    },
    async fetchStatus() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${this.apiBase()}/vehicle-access/status`, {
          headers: this.authHeaders(),
          params: { kind: this.kind, q: this.search || undefined },
        })
        this.inside = data.inside || []
        this.outside = data.outside || []
        this.counts = data.counts || this.counts
        this.staleAfterHours = data.stale_after_hours || 24
        this.cooldownSeconds = data.min_seconds_between_reads ?? this.cooldownSeconds
        this.sync = data.sync || this.sync
      } catch (err) {
        this.handleError(err, 'No se pudo cargar el estado de acceso vehicular.')
      } finally {
        this.loading = false
      }
    },
    async fetchEvents() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${this.apiBase()}/vehicle-access/events`, {
          headers: this.authHeaders(),
          params: {
            kind: this.kind,
            q: this.search || undefined,
            start: this.logStart || undefined,
            end: this.logEnd || undefined,
            category: this.logCategory,
            direction: this.logDirection,
            limit: this.limit,
            offset: this.offset,
          },
        })
        this.events = data.results || []
        this.eventsTotal = data.total || 0
      } catch (err) {
        this.handleError(err, 'No se pudo cargar la bitácora.')
      } finally {
        this.loading = false
      }
    },
    async syncNow() {
      this.syncing = true
      this.error = ''
      try {
        const { data } = await axios.post(
          `${this.apiBase()}/vehicle-access/sync`,
          {},
          { headers: this.authHeaders() },
        )
        this.sync = data.sync || this.sync
        this.reload()
      } catch (err) {
        this.handleError(err, 'No se pudo contactar el controlador de la pluma.')
      } finally {
        this.syncing = false
      }
    },
    /**
     * Badges are enrolled from the barrier feed, so one seen before its unit or
     * employee existed stays unlinked. This replays the linking for the whole
     * registry, which is what an operator needs after enrolling new units.
     */
    async resyncCredentials() {
      this.resyncing = true
      this.error = ''
      try {
        const { data } = await axios.post(
          `${this.apiBase()}/vehicle-access/credentials/resync`,
          {},
          { headers: this.authHeaders() },
        )
        this.flashNotice(
          `Credenciales revisadas: ${data.total}. ` +
            `${data.relinked} vinculadas, ${data.reclassified} reclasificadas.`,
        )
        this.reload()
      } catch (err) {
        this.handleError(err, 'No se pudieron re-vincular las credenciales.')
      } finally {
        this.resyncing = false
      }
    },
    flashNotice(message) {
      this.notice = message
      if (this.noticeTimer) clearTimeout(this.noticeTimer)
      this.noticeTimer = setTimeout(() => {
        this.notice = ''
      }, 6000)
    },
    changePage(step) {
      const next = this.offset + step * this.limit
      if (next < 0 || next >= this.eventsTotal) return
      this.offset = next
      this.fetchEvents()
    },
    /**
     * Units read better by brand and sub-brand than by their badge number,
     * which already appears as the row title ("UNIDAD 280013").
     */
    subtitleFor(row) {
      if (row.kind === 'vehicle') {
        return row.unit_description || row.unit_plates || 'Sin datos de la unidad'
      }
      return row.credential_ident
    },
    kindLabel(kind) {
      if (kind === 'vehicle') return 'Unidad'
      if (kind === 'person') return 'Persona'
      if (kind === 'unknown') return 'Sin clasificar'
      return ''
    },
    directionLabel(direction) {
      if (direction === 'in') return 'Entrada'
      if (direction === 'out') return 'Salida'
      return 'Desconocido'
    },
    /**
     * `toISOString` would report the UTC day, which runs ahead of the local one
     * for the last six hours of every evening and would default the log to a
     * day with no events yet.
     */
    localDate(value) {
      const pad = (part) => String(part).padStart(2, '0')
      return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
    },
    formatClock(value) {
      if (!value) return '—'
      return new Date(value).toLocaleTimeString('es-MX', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('es-MX', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
    formatElapsed(minutes) {
      const total = Number(minutes) || 0
      if (total < 1) return 'hace un momento'
      if (total < 60) return `hace ${total} min`
      const hours = Math.floor(total / 60)
      if (hours < 24) {
        const rest = total % 60
        return rest ? `hace ${hours} h ${rest} min` : `hace ${hours} h`
      }
      const days = Math.floor(hours / 24)
      return days === 1 ? 'hace 1 día' : `hace ${days} días`
    },
  },
}
</script>

<!--
  Selectors are namespaced under `.vac` on purpose: Bootstrap is loaded globally
  and owns `.row`, `.btn`, `.table` and friends, so unprefixed names inherit its
  gutters and light-theme colors.
-->
<style scoped>
.vac {
  --vac-brand: #ffc434;
  --vac-brand-ink: #4d3600;
  --vac-strong: #0b1220;
  --vac-ink: #1e2a44;
  --vac-muted: #5b667f;
  --vac-faint: #8a93a8;
  --vac-line: rgba(15, 27, 51, 0.1);
  --vac-line-soft: rgba(15, 27, 51, 0.07);
  --vac-panel: rgba(255, 255, 255, 0.88);
  --vac-fill: rgba(15, 27, 51, 0.035);
  --vac-in: #12b76a;
  --vac-in-ink: #027a48;
  --vac-out: #667085;
  --vac-info: #2563eb;
  --vac-info-ink: #1d4ed8;
  --vac-warn: #f79009;
  --vac-warn-ink: #b54708;
  --vac-danger: #f04438;
  --vac-danger-ink: #b42318;
  --vac-ring: rgba(37, 99, 235, 0.28);
  --vac-ease: cubic-bezier(0.2, 0.8, 0.2, 1);
  --vac-topbar: 62px;

  /* App.vue pushes #app 60px down, but twenty-odd note screens ship an
     unscoped `#app { margin-top: 0 !important }` that survives for the rest of
     the session once visited, so how far down the flow starts is not knowable.
     Anchoring the screen to the top of the document instead of the flow makes
     the top bar land in the same place either way. */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  min-height: 100vh;
  background: #eef2fb;
  color: var(--vac-ink);
  color-scheme: light;
  /* App.vue sets text-align: center on #app for the whole app. */
  text-align: left;
  padding-bottom: 56px;
}

/* The blueprint grid sits above the glow layer but below the content. */
.vac::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(15, 27, 51, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 27, 51, 0.05) 1px, transparent 1px);
  background-size: 46px 46px;
  -webkit-mask-image: radial-gradient(125% 95% at 50% 0%, #000 22%, transparent 78%);
  mask-image: radial-gradient(125% 95% at 50% 0%, #000 22%, transparent 78%);
}
/* Viewport-fixed so the glow stays put while the board scrolls past it. Kept
   transparent: the base colour belongs to .vac. */
.vac__aura {
  position: fixed;
  inset: -18%;
  z-index: 0;
  pointer-events: none;
  background:
    radial-gradient(36% 30% at 16% 8%, rgba(255, 196, 52, 0.3), transparent 70%),
    radial-gradient(34% 28% at 86% 4%, rgba(37, 99, 235, 0.16), transparent 70%),
    radial-gradient(40% 34% at 62% 96%, rgba(18, 183, 106, 0.12), transparent 72%);
  animation: vac-drift 26s ease-in-out infinite alternate;
}
.vac__body {
  position: relative;
  z-index: 2;
  max-width: 1480px;
  margin: 0 auto;
  padding: calc(var(--vac-topbar) + 24px) 24px 0;
}

/* Absolute rather than fixed, like every other screen here: the bar sits at the
   top of the screen and scrolls away with it. The body reserves its height so
   it never covers the content. */
.nav-bar-operations {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--vac-topbar);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 0 rgba(0, 0, 0, 0.05), 0 10px 26px rgba(72, 50, 8, 0.14);
}
/* Several components ship an unscoped rule that absolutely centers
   .logo-button on screen; it leaks in once any of them has been visited. */
.logo-button {
  position: static !important;
  top: auto !important;
  left: auto !important;
  transform: none !important;
  height: 60%;
  width: auto;
  object-fit: contain;
}

/* ---------- Command header ---------- */
.cmd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 16px 18px;
  border: 1px solid var(--vac-line);
  border-radius: 20px;
  background:
    linear-gradient(120deg, rgba(255, 196, 52, 0.16), transparent 44%),
    var(--vac-panel);
  backdrop-filter: blur(14px);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
  animation: vac-rise 0.5s var(--vac-ease) both;
}
.cmd__id {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.cmd__glyph {
  position: relative;
  width: 46px;
  height: 46px;
  flex: none;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-size: 1.25rem;
  background: linear-gradient(140deg, var(--vac-brand), #f0a500);
  box-shadow: 0 8px 20px rgba(240, 165, 0, 0.34);
}
.cmd__glyph::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  border: 1px solid rgba(255, 255, 255, 0.75);
  animation: vac-halo 3.2s ease-out infinite;
}
.cmd__copy { min-width: 0; }
.cmd__title {
  margin: 0;
  font-size: 1.28rem;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--vac-strong);
}
.cmd__sub {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: var(--vac-muted);
}
.cmd__sep {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--vac-faint);
}
.cmd__stamp {
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid var(--vac-line);
  background: #fff;
  font-size: 0.76rem;
  color: var(--vac-ink);
  font-variant-numeric: tabular-nums;
}
.cmd__stamp--off { color: var(--vac-warn-ink); background: #fffaeb; border-color: #fec84b; }
.cmd__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.live {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 10px 3px 8px;
  border-radius: 999px;
  border: 1px solid var(--vac-line);
  background: #fff;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--vac-muted);
  transition: color 0.25s ease, border-color 0.25s ease, background-color 0.25s ease;
}
.live i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--vac-faint);
}
.live--on {
  color: var(--vac-in-ink);
  background: #ecfdf3;
  border-color: #a6f4c5;
}
.live--on i {
  background: var(--vac-in);
  animation: vac-ping 2s ease-out infinite;
}

/* ---------- Controls ---------- */
.vac .switch {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 8px 13px 8px 10px;
  border: 1px solid var(--vac-line);
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}
.vac .switch:hover { border-color: rgba(15, 27, 51, 0.22); }
.vac .switch input {
  position: absolute;
  opacity: 0;
  width: 1px;
  height: 1px;
}
.switch__track {
  position: relative;
  width: 34px;
  height: 19px;
  border-radius: 999px;
  background: #dde1e9;
  transition: background-color 0.24s ease, box-shadow 0.24s ease;
}
.switch__knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
  transition: transform 0.26s var(--vac-ease);
}
.switch__text {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--vac-muted);
  transition: color 0.2s ease;
}
.vac .switch input:checked + .switch__track { background: var(--vac-in); }
.vac .switch input:checked + .switch__track .switch__knob { transform: translateX(15px); }
.vac .switch input:focus-visible + .switch__track {
  box-shadow: 0 0 0 3px var(--vac-ring);
}
.vac .switch input:checked ~ .switch__text { color: var(--vac-strong); }

.vac .act {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.2s var(--vac-ease), box-shadow 0.2s ease,
    background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}
.vac .act--sm { padding: 8px 13px; font-size: 0.82rem; }
.vac .act:hover:not(:disabled) { transform: translateY(-2px); }
.vac .act:active:not(:disabled) { transform: translateY(0) scale(0.985); }
.vac .act:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--vac-ring);
}
.vac .act:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.vac .act--brand {
  background: linear-gradient(180deg, #ffd45f, var(--vac-brand));
  color: var(--vac-brand-ink);
  box-shadow: 0 8px 20px rgba(240, 165, 0, 0.3);
}
.vac .act--brand:hover:not(:disabled) { box-shadow: 0 14px 28px rgba(240, 165, 0, 0.4); }
.vac .act--ghost {
  background: #fff;
  border-color: var(--vac-line);
  color: var(--vac-ink);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
}
.vac .act--ghost:hover:not(:disabled) {
  background: #f7f9fc;
  border-color: rgba(15, 27, 51, 0.22);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}
.act__spin {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  border: 2px solid rgba(77, 54, 0, 0.25);
  border-top-color: var(--vac-brand-ink);
  animation: vac-spin 0.7s linear infinite;
}

.scan {
  position: relative;
  height: 2px;
  margin-top: 14px;
  border-radius: 999px;
  background: rgba(15, 27, 51, 0.08);
  overflow: hidden;
}
.scan::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: 34%;
  background: linear-gradient(90deg, transparent, var(--vac-brand), transparent);
  animation: vac-scan 1.15s ease-in-out infinite;
}

/* ---------- Banners ---------- */
.banner {
  margin: 14px 0 0;
  padding: 11px 15px;
  border-radius: 14px;
  border: 1px solid;
  font-size: 0.87rem;
}
.banner--error { background: #fef3f2; border-color: #fda29b; color: var(--vac-danger-ink); }
.banner--warn { background: #fffaeb; border-color: #fec84b; color: var(--vac-warn-ink); }
.banner--ok { background: #ecfdf3; border-color: #6ce9a6; color: var(--vac-in-ink); }

/* ---------- KPI strip ---------- */
.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 14px;
  margin-top: 16px;
}
.stat {
  --accent: var(--vac-out);
  position: relative;
  overflow: hidden;
  padding: 15px 17px 16px;
  border: 1px solid var(--vac-line);
  border-radius: 18px;
  background: var(--vac-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  transition: transform 0.28s var(--vac-ease), border-color 0.28s ease, box-shadow 0.28s ease;
  animation: vac-rise 0.55s var(--vac-ease) both;
}
.stat::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, var(--accent), transparent 78%);
}
.stat:hover {
  transform: translateY(-4px);
  border-color: rgba(15, 27, 51, 0.18);
  box-shadow: 0 20px 44px rgba(15, 23, 42, 0.14);
}
.stat--fleet { --accent: var(--vac-in); }
.stat--crew { --accent: var(--vac-info); }
.stat--total { --accent: var(--vac-brand); }
.stat--alert { --accent: var(--vac-danger); }
.stats .stat:nth-child(2) { animation-delay: 0.06s; }
.stats .stat:nth-child(3) { animation-delay: 0.12s; }
.stats .stat:nth-child(4) { animation-delay: 0.18s; }

.stat__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.stat__label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vac-muted);
}
.stat__glyph {
  width: 26px;
  height: 26px;
  flex: none;
  border-radius: 8px;
  display: grid;
  place-items: center;
  font-size: 0.82rem;
  background: #fff;
  border: 1px solid var(--vac-line);
}
.stat__slot {
  position: relative;
  height: 2.5rem;
  margin-top: 6px;
}
.stat__value {
  display: block;
  font-size: 2.15rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: var(--vac-strong);
  font-variant-numeric: tabular-nums;
}
.stat--alert .stat__value { color: var(--vac-danger-ink); }
.stat__hint {
  display: block;
  margin-top: 9px;
  font-size: 0.76rem;
  color: var(--vac-faint);
}
/* Sized to match the meter row so every card's hint line stays aligned. */
.stat__flags { display: flex; align-items: center; height: 15px; }
.flag {
  display: inline-flex;
  align-items: center;
  padding: 1px 9px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  color: var(--vac-danger-ink);
  background: #fef3f2;
  border: 1px solid #fda29b;
  animation: vac-throb 1.9s ease-in-out infinite;
}
.flag--calm {
  color: var(--vac-faint);
  background: var(--vac-fill);
  border-color: var(--vac-line);
  animation: none;
}

.meter {
  display: flex;
  height: 5px;
  margin: 5px 0;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(15, 27, 51, 0.07);
}
.meter__fill,
.meter__rest {
  flex-basis: 0;
  transition: flex-grow 0.6s var(--vac-ease);
}
.meter__fill {
  background-color: var(--accent);
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.35), transparent);
}
.meter__rest { background: #d0d5dd; }

/* ---------- Toolbar ---------- */
.bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 20px;
}
.seg {
  position: relative;
  display: inline-flex;
  padding: 4px;
  border: 1px solid var(--vac-line);
  border-radius: 14px;
  background: rgba(15, 27, 51, 0.05);
}
.seg__thumb {
  position: absolute;
  top: 4px;
  left: 4px;
  bottom: 4px;
  width: calc(50% - 4px);
  border-radius: 10px;
  background: linear-gradient(180deg, #ffd45f, var(--vac-brand));
  box-shadow: 0 4px 12px rgba(240, 165, 0, 0.34);
  transition: transform 0.34s var(--vac-ease);
}
.seg__btn {
  position: relative;
  z-index: 1;
  min-width: 108px;
  padding: 8px 18px;
  border: none;
  border-radius: 10px;
  background: transparent;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--vac-muted);
  cursor: pointer;
  transition: color 0.24s ease;
}
.seg__btn:hover { color: var(--vac-strong); }
.seg__btn.is-active { color: var(--vac-brand-ink); }
.seg__btn:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--vac-ring); }

.filters { display: flex; gap: 10px; flex-wrap: wrap; }
.field { position: relative; display: flex; align-items: center; }
.field__icon {
  position: absolute;
  left: 12px;
  width: 15px;
  height: 15px;
  color: var(--vac-faint);
  pointer-events: none;
  transition: color 0.2s ease;
}
.field:focus-within .field__icon { color: var(--vac-info); }
.vac input.field__input { padding-left: 34px; min-width: 268px; }

.vac .input {
  appearance: none;
  -webkit-appearance: none;
  padding: 10px 13px;
  border: 1px solid var(--vac-line);
  border-radius: 12px;
  background: #fff;
  color: var(--vac-ink);
  font-size: 0.85rem;
  min-width: 150px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.05);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}
.vac .input::placeholder { color: var(--vac-faint); }
.vac .input:hover:not(:disabled) { border-color: rgba(15, 27, 51, 0.22); }
.vac .input:focus {
  outline: none;
  border-color: rgba(37, 99, 235, 0.55);
  box-shadow: 0 0 0 3px var(--vac-ring);
}
.vac .input:disabled { background: #f2f4f7; color: var(--vac-faint); cursor: not-allowed; }
.vac .input--select {
  min-width: 148px;
  padding-right: 32px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.6 6 6.4 11 1.6' fill='none' stroke='%235b667f' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 11px;
  cursor: pointer;
}
.vac .input--date { min-width: 138px; font-variant-numeric: tabular-nums; }

/* ---------- Board ---------- */
.zone { margin-top: 24px; animation: vac-rise 0.6s var(--vac-ease) both; }
.zone__head {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 12px;
}
.zone__glyph {
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 9px;
  display: grid;
  place-items: center;
  font-size: 0.88rem;
  background: #fff;
  border: 1px solid var(--vac-line);
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
}
.zone__title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: var(--vac-strong);
}
.zone__chips { display: flex; gap: 7px; margin-left: auto; flex-wrap: wrap; }
.chip {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.73rem;
  font-weight: 700;
  border: 1px solid var(--vac-line);
  background: #fff;
  font-variant-numeric: tabular-nums;
}
.chip--in { color: var(--vac-in-ink); background: #ecfdf3; border-color: #a6f4c5; }
.chip--out { color: var(--vac-out); }

.lanes {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 16px;
}
.lane {
  position: relative;
  padding: 14px;
  border: 1px solid var(--vac-line);
  border-radius: 20px;
  background: var(--vac-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
}
.lane__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 11px;
  border-bottom: 1px solid var(--vac-line);
}
.lane__title {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--vac-muted);
}
/* A light sweep on the "inside" lane signals that the feed is being polled. */
.lane--in.is-live .lane__head::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  height: 1px;
  width: 34%;
  background: linear-gradient(90deg, transparent, var(--vac-in), transparent);
  animation: vac-sweep 3.6s ease-in-out infinite;
}
.pill {
  min-width: 26px;
  padding: 3px 9px;
  border-radius: 999px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.pill--in { background: #d1fadf; color: var(--vac-in-ink); box-shadow: inset 0 0 0 1px rgba(18, 183, 106, 0.3); }
.pill--out { background: #f2f4f7; color: #475467; box-shadow: inset 0 0 0 1px var(--vac-line); }

.entries {
  position: relative;
  list-style: none;
  display: grid;
  gap: 7px;
  margin: 11px 0 0;
  padding: 0;
}
.entry {
  position: relative;
  display: grid;
  grid-template-columns: 3px 36px 1fr auto;
  align-items: center;
  gap: 11px;
  padding: 10px 12px 10px 0;
  border: 1px solid var(--vac-line-soft);
  border-radius: 14px;
  background: #fafbfd;
  overflow: hidden;
  transition: transform 0.22s var(--vac-ease), background-color 0.22s ease,
    border-color 0.22s ease, box-shadow 0.22s ease;
}
.entry:hover {
  transform: translateX(3px);
  background: #fff;
  border-color: rgba(15, 27, 51, 0.16);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.1);
}
.entry__rail {
  align-self: stretch;
  border-radius: 0 3px 3px 0;
  background: #b6bfd0;
  opacity: 0.7;
  transition: opacity 0.24s ease, background-color 0.24s ease;
}
.entry:hover .entry__rail { opacity: 1; }
.lane--in .entry__rail { background: var(--vac-in); }
.entry--stale .entry__rail { background: var(--vac-warn); opacity: 1; }
.entry--stale {
  background: #fffaeb;
  border-color: #fec84b;
}
.entry__badge {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  font-size: 0.98rem;
  background: #f2f4f7;
  border: 1px solid var(--vac-line);
}
.entry__badge--vehicle { background: #e0eaff; border-color: rgba(37, 99, 235, 0.26); }
.entry__badge--person { background: #f4ebff; border-color: rgba(127, 86, 217, 0.26); }
.entry--stale .entry__badge::after {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  border: 1px solid var(--vac-warn);
  animation: vac-throb 1.9s ease-in-out infinite;
}
.entry__body { display: grid; gap: 2px; min-width: 0; }
.entry__name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--vac-strong);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.entry__meta {
  font-size: 0.76rem;
  color: var(--vac-faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.entry__when { display: grid; justify-items: end; gap: 2px; }
.entry__clock {
  font-size: 0.88rem;
  font-weight: 600;
  color: var(--vac-ink);
  font-variant-numeric: tabular-nums;
}
.entry__ago { font-size: 0.72rem; color: var(--vac-faint); white-space: nowrap; }

.empty {
  margin: 11px 0 0;
  padding: 22px 12px;
  border: 1px dashed rgba(15, 27, 51, 0.16);
  border-radius: 14px;
  text-align: center;
  font-size: 0.83rem;
  color: var(--vac-faint);
}

/* ---------- Log ---------- */
.log {
  margin-top: 18px;
  border: 1px solid var(--vac-line);
  border-radius: 20px;
  background: var(--vac-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  animation: vac-rise 0.5s var(--vac-ease) both;
}
/* Bounded height so the sticky header has something to stick to. */
.log__scroll {
  max-height: min(70vh, 760px);
  overflow: auto;
  border-radius: 20px 20px 0 0;
}
.vac .logtable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.85rem;
  color: var(--vac-ink);
}
.vac .logtable th {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 13px 14px;
  text-align: left;
  font-size: 0.71rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--vac-muted);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid var(--vac-line);
  backdrop-filter: blur(10px);
}
.vac .logtable td {
  padding: 11px 14px;
  border-bottom: 1px solid var(--vac-line-soft);
  vertical-align: middle;
}
/* Fresh keys on every fetch remount the rows, so the cascade replays. */
.vac .logtable tbody tr {
  transition: background-color 0.2s ease;
  animation: vac-row 0.42s var(--vac-ease) both;
}
.vac .logtable tbody tr:nth-child(2) { animation-delay: 0.02s; }
.vac .logtable tbody tr:nth-child(3) { animation-delay: 0.04s; }
.vac .logtable tbody tr:nth-child(4) { animation-delay: 0.06s; }
.vac .logtable tbody tr:nth-child(5) { animation-delay: 0.08s; }
.vac .logtable tbody tr:nth-child(6) { animation-delay: 0.1s; }
.vac .logtable tbody tr:nth-child(7) { animation-delay: 0.12s; }
.vac .logtable tbody tr:nth-child(8) { animation-delay: 0.14s; }
.vac .logtable tbody tr:nth-child(9) { animation-delay: 0.16s; }
.vac .logtable tbody tr:nth-child(n + 10) { animation-delay: 0.18s; }
.vac .logtable tbody tr:hover td { background: #f4f7fc; }
.vac .logtable tbody tr.is-context td { color: var(--vac-muted); background: rgba(15, 27, 51, 0.02); }
.vac .logtable tbody tr.is-context:hover td { background: #f4f7fc; }
.logtable__blank td { border-bottom: none; }
.logtable__blank .empty { margin: 12px; }
.mono {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.cell-name { display: block; font-weight: 700; color: var(--vac-strong); }
.cell-kind { display: block; margin-top: 1px; font-size: 0.74rem; color: var(--vac-faint); }
.cell-muted { color: var(--vac-faint); }

.tag {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  white-space: nowrap;
  border: 1px solid transparent;
}
.tag--in { background: #ecfdf3; color: var(--vac-in-ink); border-color: #a6f4c5; }
.tag--out { background: #fef3f2; color: var(--vac-danger-ink); border-color: #fecdca; }
.tag--unknown,
.tag--ok { background: #f2f4f7; color: #475467; border-color: #e4e7ec; }
.tag--deny { background: #fef3f2; color: var(--vac-danger-ink); border-color: #fda29b; }
.tag--repeat {
  background: #fffaeb;
  color: var(--vac-warn-ink);
  border-color: #fec84b;
  cursor: help;
}
.tag--door { background: #eff4ff; color: var(--vac-info-ink); border-color: #b2ccff; }
.tag--system { background: #f8f9fb; color: var(--vac-muted); border-color: #e4e7ec; }

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 13px;
  padding: 13px 14px;
  border-top: 1px solid var(--vac-line);
}
.pager__info { font-size: 0.8rem; color: var(--vac-muted); }

/* ---------- Transitions ---------- */
.entry-enter-active { transition: opacity 0.34s ease, transform 0.34s var(--vac-ease); }
.entry-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  transition: opacity 0.24s ease, transform 0.24s ease;
}
.entry-enter-from { opacity: 0; transform: translateX(-16px) scale(0.98); }
.entry-leave-to { opacity: 0; transform: translateX(16px) scale(0.98); }
.entry-move { transition: transform 0.36s var(--vac-ease); }

.tick-enter-active,
.tick-leave-active { transition: opacity 0.3s ease, transform 0.3s var(--vac-ease); }
.tick-leave-active { position: absolute; top: 0; left: 0; }
.tick-enter-from { opacity: 0; transform: translateY(65%); }
.tick-leave-to { opacity: 0; transform: translateY(-65%); }

.drop-enter-active,
.drop-leave-active { transition: opacity 0.28s ease, transform 0.28s var(--vac-ease); }
.drop-enter-from,
.drop-leave-to { opacity: 0; transform: translateY(-8px); }

/* ---------- Keyframes ---------- */
@keyframes vac-rise {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
}
@keyframes vac-row {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
}
@keyframes vac-drift {
  from { transform: translate3d(-1.5%, -1%, 0) scale(1); }
  to { transform: translate3d(2%, 1.5%, 0) scale(1.07); }
}
@keyframes vac-spin {
  to { transform: rotate(360deg); }
}
@keyframes vac-scan {
  0% { transform: translateX(-110%); }
  100% { transform: translateX(400%); }
}
@keyframes vac-sweep {
  0% { transform: translateX(-20%); opacity: 0; }
  18% { opacity: 1; }
  78% { opacity: 1; }
  100% { transform: translateX(300%); opacity: 0; }
}
@keyframes vac-ping {
  0% { box-shadow: 0 0 0 0 rgba(18, 183, 106, 0.5); }
  70% { box-shadow: 0 0 0 7px rgba(18, 183, 106, 0); }
  100% { box-shadow: 0 0 0 0 rgba(18, 183, 106, 0); }
}
@keyframes vac-halo {
  0% { opacity: 0.7; transform: scale(1); }
  70% { opacity: 0; transform: scale(1.28); }
  100% { opacity: 0; transform: scale(1.28); }
}
@keyframes vac-throb {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.42; }
}

@media (max-width: 720px) {
  .vac__body { padding: calc(var(--vac-topbar) + 18px) 14px 0; }
  .cmd { padding: 14px; }
  /* A grid instead of a flex row: `flex: 1` items floor at their min-content
     width and overflow the viewport rather than wrapping. */
  .cmd__actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
  .vac .cmd__actions .act { width: 100%; justify-content: center; }
  .vac .cmd__actions .switch { justify-content: center; }
  .stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .seg { width: 100%; }
  .seg__btn { flex: 1; min-width: 0; }
  .filters { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .field { grid-column: 1 / -1; }
  .vac input.field__input { min-width: 0; width: 100%; }
  .vac .input { min-width: 0; }
  .lanes { grid-template-columns: 1fr; }
}

@media (prefers-reduced-motion: reduce) {
  .vac *,
  .vac *::before,
  .vac *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }
}
</style>
